import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { PERMISSION } from 'src/features/auth/types';
import { ENTITY_TYPE } from 'src/features/entity/types';
import CellLinkWithIcon from 'src/features/membership/components/cell/CellLinkWIthIcon';
import { PATH } from 'src/features/navigation/utils/constants';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { useListValidations } from 'src/features/validations/api/useListValidations';
import { validationMessages } from 'src/features/validations/utils/messages';
import { Validation } from 'src/features/validations/utils/types';
import { validationTypeBadgeOptions } from 'src/features/validationTypes/utils/constants';
import { validationTypesMessages } from 'src/features/validationTypes/utils/messages';
import { VALIDATION_TYPE } from 'src/features/validationTypes/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { GetDurationFromMinutes } from 'src/utils/getDurationFromMinutes';

const sortingOptions = { initialSortingState: [{ id: 'created_at', desc: true }] };

type DateParams = {
    from_datetime: string;
    to_datetime: string;
};

type ValidationsTableAccountDashboardProps = {
    dateParams?: DateParams;
    active_stay?: string;
    past_stay?: string;
};

const ValidationsTableAccountDashboard = ({
    dateParams,
    active_stay,
    past_stay,
}: ValidationsTableAccountDashboardProps) => {
    const { formatMessage, formatNumber } = useIntl();
    const { accountId } = useParams();
    const { parkingId } = useParking();
    const settingsData = React.useContext(SettingsContext);

    const filterDateParams = React.useMemo(() => {
        return {
            created_at_after: dateParams?.from_datetime,
            created_at_before: dateParams?.to_datetime,
        };
    }, [dateParams]);

    const accessActiveStays = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);
    const accessPastStays = useHasPermissions([PERMISSION.ACCESS_PAST_STAYS]);
    const accessEntities = useHasPermissions([PERMISSION.ACCESS_ENTITIES]);
    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);

    const payload = React.useMemo(
        () => ({
            parkingId,
            ...(accountId && { validator_account: accountId }),
            ...(active_stay && { active_stay }),
            ...(past_stay && { past_stay }),
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
            ...filterDateParams,
        }),
        [
            querySearchOptions,
            querySortingOptions,
            queryPaginationOptions,
            accountId,
            parkingId,
            filterDateParams,
            active_stay,
            past_stay,
        ],
    );

    const { data: validations, isLoading } = useListValidations(payload);

    const columnHelper = createColumnHelper<Validation>();

    const columns = [
        columnHelper.accessor('created_at', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(paymentsMessages.timestamp),
            },
        }),
        columnHelper.accessor('type.type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const name = ctx.row.original.type.name;
                return (
                    <div className="flex flex-row gap-2">
                        <div className="whitespace-nowrap">{name}</div>
                        <CellBadge {...ctx} />
                    </div>
                );
            },
            meta: {
                title: formatMessage(validationTypesMessages.validationType),
                badgeOptions: validationTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const validationType: VALIDATION_TYPE = row.original.type.type;
                const validationAmount = row.original.amount ?? 0;

                const typeCentsAmount =
                    validationType === VALIDATION_TYPE.CHARGE &&
                    formatNumber(validationAmount / 100, {
                        style: 'currency',
                        currency: row.original.type.currency ?? settingsData.default_currency,
                    });

                const typeMinutesAmount = GetDurationFromMinutes(validationAmount);

                let displayAmount;
                switch (validationType) {
                    case VALIDATION_TYPE.CHARGE:
                        displayAmount = typeCentsAmount;
                        break;
                    case VALIDATION_TYPE.GUEST:
                        displayAmount = '-';
                        break;
                    case VALIDATION_TYPE.WAIVE:
                    case VALIDATION_TYPE.PRICING_PLAN_CHANGE:
                        displayAmount = '-';
                        break;
                    default:
                        displayAmount = typeMinutesAmount;
                }

                return displayAmount;
            },
            meta: {
                title: formatMessage(validationTypesMessages.validationTypeAmount),
            },
            minSize: 120,
        }),
        columnHelper.accessor('validated_by.name', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const validateByUser = row.original.validated_by?.name;
                const validatedByCloudUser = row.original.validated_by_cloud_user?.name;

                const validatedBy = validateByUser ? validateByUser : validatedByCloudUser;
                return validatedBy ?? '-';
            },
            meta: {
                title: formatMessage(validationTypesMessages.validatedBy),
            },
        }),
        columnHelper.accessor('active_stay_period.stay.ref', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                const isActiveStay = ctx.row.original.active_stay_period !== null;
                const periodStayId = isActiveStay
                    ? ctx.row.original.active_stay_period?.stay.id
                    : ctx.row.original.past_stay_period?.stay.id;
                const toPath = isActiveStay ? 'active-stay' : 'past-stay';
                const hasPermission = isActiveStay
                    ? accessActiveStays && active_stay === undefined
                    : accessPastStays && past_stay === undefined;
                const linkTitle = isActiveStay
                    ? ctx.row.original.active_stay_period?.stay.ref
                    : ctx.row.original.past_stay_period?.stay.ref;

                return periodStayId ? (
                    <CellLinkWithIcon
                        toPath={`/${PATH.VISITORS}/${toPath}/dashboard/${periodStayId}`}
                        linkTitle={linkTitle}
                        hasPermissions={hasPermission}
                    />
                ) : (
                    '-'
                );
            },
            meta: {
                title: formatMessage(validationMessages.stay),
            },
        }),
        columnHelper.accessor('main_identifier.value', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(validationTypesMessages.mainIdentifier),
            },
        }),
        columnHelper.accessor('visitor.owner.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                return (
                    <CellLinkWithIcon
                        linkTitle={ctx.row.original.visitor?.owner?.name}
                        toPath={`/${PATH.ENTITIES}/${ENTITY_TYPE.PERSON}/${ctx.row.original.visitor?.owner?.id}`}
                        hasPermissions={accessEntities}
                    />
                );
            },
            meta: {
                title: formatMessage(validationTypesMessages.owner),
            },
        }),
        columnHelper.accessor('visitor.main_identifier.value', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                return (
                    <CellLinkWithIcon
                        linkTitle={ctx.row.original.visitor?.main_identifier?.value}
                        toPath={`/${PATH.VISITORS}/visitor/${ctx.row.original.visitor?.id}`}
                        hasPermissions={accessVisitors}
                    />
                );
            },
            meta: {
                title: formatMessage(validationTypesMessages.visitor),
            },
        }),
    ];

    return (
        <DataTable<Validation, any>
            isLoading={isLoading}
            columns={columns}
            data={validations}
            search={search}
            sorting={sorting}
            pagination={pagination}
            enableSearchParams={false}
            semantics={{
                title: formatMessage(validationMessages.validations),
                entryName: formatMessage(validationMessages.validation).toLowerCase(),
                entryNames: formatMessage(validationMessages.validations).toLowerCase(),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
};

export default ValidationsTableAccountDashboard;
